import { isArray, isEqual, isPlainObject, mapValues, omitBy } from 'lodash-es';
import { useMemo, useState } from 'react';

/**
 * Values that should be considered equivalent when comparing properties of objects that hold values of form fields.
 * These values are also considered equivalent to missing properties. For example, `{ x: 1, y: undefined }` is considered equivalent to `{ x: 1 }`.
 */
const EMPTY_VALUES = ['', undefined, null];

/** Similar to Lodash's `cloneDeep`, but also removes properties that are considered "empty" (see `EMPTY_VALUES`). */
const deepRemoveEmptyValues = <T extends object | null | undefined>(value: T): T => {
    if (isArray(value)) {
        return value.map((item) => deepRemoveEmptyValues(item)) as T;
    }

    if (isPlainObject(value)) {
        const cleaned = mapValues(value, (v) => deepRemoveEmptyValues(v));
        return omitBy(cleaned, (value) => EMPTY_VALUES.includes(value)) as T;
    }

    return value;
};

/** Compares all properties of two objects after removing any properties that are considered "empty" (see `EMPTY_VALUES`). */
const formFieldValuesAreEquivalent = <T extends object | null | undefined>(formFieldValuesA: T, formFieldValuesB: T): boolean => {
    return isEqual(deepRemoveEmptyValues(formFieldValuesA), deepRemoveEmptyValues(formFieldValuesB));
};

/**
 * A utility for consistently tracking whether a form has unsaved changes, so the user can be visually informed when the state of the UI does not reflect the state of the database.
 * To use this hook:
 * * Store all form state (except files) in a single object.
 * * If the form involves managing files, use `useFileManagementArea` or `useFileManagementAreas` above the usage of this hook, and pass the value returned from that hook as the argument for `hasUnsavedFileChanges`.
 * * Below the save button(s) for the form, render an `UnsavedChangesWarning`, which will show/hide itself according to the boolean returned from this hook.
 *
 * @param formFieldValues - the current values of form fields in a form, excluding file management areas.
 * @param hasUnsavedFileChanges - whether there are any file management areas in the form that have unsaved changes. This argument must be the value returned by `useFileManagementArea` or `useFileManagementAreas`.
 * @returns a boolean indicating whether a form has any unsaved changes, and a function to be called whenever the form is saved. The function must be called with the values of the form fields as stored after the successful save.
 */
export const useUnsavedChangesTracking = <T extends object | null | undefined>(formFieldValues: T, hasUnsavedFileChanges: boolean): readonly [boolean, (savedValues: T) => void] => {
    const [lastSavedFormFieldsState, setLastSavedFormFieldsState] = useState<T>(formFieldValues);
    const hasUnsavedChanges = useMemo(() => !formFieldValuesAreEquivalent(formFieldValues, lastSavedFormFieldsState) || hasUnsavedFileChanges, [formFieldValues, lastSavedFormFieldsState, hasUnsavedFileChanges]);
    const onFormFieldValuesSaved = (savedValues: T) => setLastSavedFormFieldsState(savedValues);
    return [hasUnsavedChanges, onFormFieldValuesSaved];
};
